import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../messages/ThreadQuill.css';
import {
  addAdminComment,
  deleteAdminComment,
  addMyComment,
  deleteMyComment,
} from './store/checklistSlice';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const isQuillEmpty = val => !val || val.replace(/<[^>]*>/g, '').trim() === '';

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TaskCommentPanel = ({ task: initialTask, playerId, userRole, onClose }) => {
  const dispatch = useDispatch();
  const [task, setTask] = useState(initialTask);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const commentsContainerRef = useRef(null);
  const bottomRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  // Sync when Redux updates the task externally
  useEffect(() => {
    setTask(initialTask);
  }, [initialTask]);

  const commentsCount = (task.comments || []).length;

  // Scroll to bottom only when user is already near the bottom
  useEffect(() => {
    if (shouldAutoScrollRef.current && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [commentsCount]);

  const handleCommentsScroll = () => {
    const el = commentsContainerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = distanceFromBottom < 80;
  };

  const sortedComments = [...(task.comments || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const handleSubmit = async () => {
    if (isQuillEmpty(text) || submitting) return;
    setSubmitting(true);
    let updated = null;
    if (userRole === 'admin') {
      updated = await dispatch(addAdminComment(playerId, task._id, text));
    } else {
      updated = await dispatch(addMyComment(task._id, text));
    }
    if (updated) setTask(updated);
    setText('');
    setSubmitting(false);
  };

  const handleDelete = async (commentId) => {
    let updated = null;
    if (userRole === 'admin') {
      updated = await dispatch(deleteAdminComment(playerId, task._id, commentId));
    } else {
      updated = await dispatch(deleteMyComment(task._id, commentId));
    }
    if (updated) setTask(updated);
  };

  const canDelete = (comment) => {
    if (userRole === 'admin') return comment.authorRole === 'admin' || comment.authorRole === 'assistant';
    return comment.authorRole === 'athlete';
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '10px',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <i className="fas fa-comments" style={{ color: '#2e496b', fontSize: '15px', flexShrink: 0 }} />
            <span
              style={{
                fontWeight: '600',
                fontSize: '14px',
                color: '#1e293b',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={task.label}
            >
              {task.label}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              color: '#64748b',
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Zamknij"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Comment list */}
        <div
          ref={commentsContainerRef}
          onScroll={handleCommentsScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '14px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {sortedComments.length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: '14px', textAlign: 'center', margin: 'auto' }}>
              Brak komentarzy. Napisz pierwszy!
            </p>
          )}
          {sortedComments.map(comment => {
            const isAgencySide = comment.authorRole === 'admin' || comment.authorRole === 'assistant';
            const shouldMaskAgencyAuthor = userRole === 'player' && isAgencySide;
            const senderBadge = comment.authorRole === 'assistant'
              ? 'agencja'
              : (comment.authorRole === 'admin' ? 'Kajetan Borecki' : 'Zawodnik');
            return (
              <div
                key={comment._id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isAgencySide ? 'flex-start' : 'flex-end',
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    background: isAgencySide ? '#eff6ff' : '#f0fdf4',
                    borderRadius: isAgencySide ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                    padding: '8px 12px',
                    position: 'relative',
                  }}
                >
                  {/* Author + date */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '4px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: isAgencySide ? '#1e40af' : '#15803d',
                        background: isAgencySide ? '#dbeafe' : '#dcfce7',
                        borderRadius: '3px',
                        padding: '1px 6px',
                      }}
                    >
                      {senderBadge}
                    </span>
                    {!shouldMaskAgencyAuthor && (
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        {comment.author}
                      </span>
                    )}
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                      · {formatDate(comment.createdAt)}
                    </span>
                    {canDelete(comment) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(comment._id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#e53e3e',
                          cursor: 'pointer',
                          fontSize: '11px',
                          padding: '0 2px',
                          marginLeft: '2px',
                        }}
                        title="Usuń komentarz"
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    )}
                  </div>
                  {/* Text */}
                  {/* eslint-disable-next-line react/no-danger */}
                  <div
                    className="msg-bubble-content"
                    style={{ fontSize: '13px', color: '#1e293b', wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: comment.text }}
                  />
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div
          style={{
            borderTop: '1px solid #e2e8f0',
            padding: '12px 18px',
            background: '#f8fafc',
          }}
        >
          <div className="thread-quill" style={{ marginBottom: '8px' }}>
            <ReactQuill
              value={text}
              onChange={setText}
              modules={quillModules}
              formats={quillFormats}
              theme="snow"
              placeholder="Napisz komentarz…"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isQuillEmpty(text) || submitting}
              style={{
                background: '#2e496b',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 18px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isQuillEmpty(text) || submitting ? 'not-allowed' : 'pointer',
                opacity: isQuillEmpty(text) || submitting ? 0.5 : 1,
              }}
            >
              <i className="fas fa-paper-plane" style={{ marginRight: '6px' }} />
              Wyślij
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCommentPanel;
